import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAuth, useCanWrite } from "../auth/AuthProvider";
import { ConfirmActionModal } from "../components/ConfirmActionModal";
import {
  Alert,
  Button,
  Mono,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  PlatformRoleBadge,
  RoleBadge,
  Select,
  SuspendedBadge,
  Table,
  Td,
  Th,
  TypedPhrase,
  buttonStyles,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { formatDateTime, formatRelative } from "../lib/format";
import {
  useCommunityMembership,
  useDeleteUser,
  useRevokeSessions,
  useUnlinkUserProvider,
  useSetCommunityRole,
  useUpdateUser,
  useUser,
} from "../lib/queries";
import type { PlatformRole } from "../lib/types";

export function UserDetailPage() {
  const { userId = "" } = useParams();
  const { data, isPending, error } = useUser(userId);
  const { user: me } = useAuth();
  const canWrite = useCanWrite();

  const update = useUpdateUser(userId);
  const revoke = useRevokeSessions(userId);
  const unlink = useUnlinkUserProvider(userId);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<"suspend" | "revoke" | null>(null);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const isSelf = me?.id === data.id;

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.email}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to={`/audit?actorUserId=${data.id}`} className={buttonStyles("secondary", "md")}>
              Audit activity
            </Link>
            {canWrite && (
              <>
                <Button
                  variant="secondary"
                  loading={revoke.isPending}
                  onClick={() => {
                    revoke.reset();
                    setConfirmation("revoke");
                  }}
                >
                  Revoke sessions
                </Button>
                {data.suspendedAt ? (
                  <Button
                    loading={update.isPending}
                    onClick={() => update.mutate({ suspended: false })}
                  >
                    Unsuspend
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    disabled={isSelf}
                    loading={update.isPending}
                    onClick={() => {
                      update.reset();
                      setConfirmation("suspend");
                    }}
                  >
                    Suspend
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {update.error && confirmation !== "suspend" && <Alert>{update.error.message}</Alert>}
      {revoke.error && confirmation !== "revoke" && <Alert>{revoke.error.message}</Alert>}
      {revoke.isSuccess && (
        <Alert tone="success">
          Revoked {revoke.data.revoked} session{revoke.data.revoked === 1 ? "" : "s"}.
        </Alert>
      )}
      {data.suspendedAt && (
        <Alert tone="warning">
          Suspended {formatRelative(data.suspendedAt)}. Sign-in and token refresh are both blocked.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Account" />
          <dl className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">
                Status
              </dt>
              <dd className="mt-1 flex flex-wrap gap-1.5 text-sm">
                {data.platformRole && <PlatformRoleBadge role={data.platformRole} />}
                {data.suspendedAt ? <SuspendedBadge /> : <span>Active</span>}
              </dd>
            </div>
            <Detail label="Active sessions" value={String(data.activeSessions)} />
            {/* How they sign in besides a password; the card below carries
                the detail and the operator's way to remove one. */}
            <Detail
              label="Signs in with"
              value={
                data.linkedAccounts.length === 0
                  ? "Password"
                  : `Password, ${data.linkedAccounts.map((a) => (a.provider === "github" ? "GitHub" : "GitLab")).join(", ")}`
              }
            />
            <Detail label="Last login" value={formatDateTime(data.lastLoginAt)} />
            <Detail label="Joined" value={formatDateTime(data.createdAt)} />
          </dl>
        </Card>

        {canWrite && <StaffCard userId={userId} current={data.platformRole} isSelf={isSelf} />}
      </div>

      {data.linkedAccounts.length > 0 && (
        <Card>
          <CardHeader
            title="Linked sign-ins"
            description="GitHub or GitLab accounts that sign in as this user. Removing one leaves the account and its password; the person can link it again from their own settings."
          />
          <ul className="divide-y divide-line">
            {data.linkedAccounts.map((account) => (
              <li key={account.provider} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <span className="font-medium text-fg">{account.provider === "github" ? "GitHub" : "GitLab"}</span>
                <span className="text-fg-subtle">
                  <Mono>{account.login}</Mono> · linked {formatRelative(account.linkedAt)} · last used {formatRelative(account.lastUsedAt)}
                </span>
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    loading={unlink.isPending && unlink.variables === account.provider}
                    onClick={() => setUnlinking(account.provider)}
                  >
                    Unlink
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {unlink.error && (
            <div className="border-t border-line px-5 py-3">
              <Alert>{unlink.error.message}</Alert>
            </div>
          )}
        </Card>
      )}

      <Card>
        <CardHeader title="Organizations" description={`Member of ${data.organizations.length}`} />
        {data.organizations.length === 0 ? (
          <EmptyState title="Not a member of any organization" />
        ) : (
          <Table
            head={
              <tr>
                <Th>Organization</Th>
                <Th>Role</Th>
                <Th priority="sm">Joined</Th>
              </tr>
            }
          >
            {data.organizations.map((org) => (
              <tr key={org.id} className="hover:bg-raised">
                <Td>
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {org.name}
                  </Link>
                  {org.suspendedAt && (
                    <span className="ml-2">
                      <SuspendedBadge />
                    </span>
                  )}
                </Td>
                <Td>
                  <RoleBadge role={org.role} />
                </Td>
                <Td className="text-fg-subtle" priority="sm">{formatDateTime(org.joinedAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <CommunityCard userId={userId} canWrite={canWrite} />

      {canWrite && !isSelf && <DangerZone userId={userId} email={data.email} />}

      <ConfirmActionModal
        open={unlinking !== null}
        onClose={() => setUnlinking(null)}
        onConfirm={() => {
          if (!unlinking) return;
          unlink.mutate(unlinking, { onSuccess: () => setUnlinking(null) });
        }}
        title={`Unlink ${unlinking === "github" ? "GitHub" : "GitLab"} from ${data.name}`}
        description="They can no longer sign in with it. The account, its password and its sessions stay as they are."
        consequence="Audited under the user. They can link a provider again from Settings → Security."
        confirmLabel="Unlink"
        loading={unlink.isPending}
        error={unlink.error}
      />
      <ConfirmActionModal
        open={confirmation !== null}
        onClose={() => setConfirmation(null)}
        onConfirm={() => {
          if (confirmation === "suspend") {
            update.mutate(
              { suspended: true },
              {
                onSuccess: () => {
                  setConfirmation(null);
                },
              },
            );
          }

          if (confirmation === "revoke") {
            revoke.mutate(undefined, {
              onSuccess: () => {
                setConfirmation(null);
              },
            });
          }
        }}
        title={
          confirmation === "suspend" ? `Suspend ${data.name}?` : `Revoke sessions for ${data.name}?`
        }
        description={
          confirmation === "suspend"
            ? "This prevents the account from signing in or refreshing an existing session."
            : "This signs the account out of every active session."
        }
        consequence={
          confirmation === "suspend"
            ? `${data.email} cannot sign in again until an operator unsuspends the account.`
            : `${data.activeSessions} active session${data.activeSessions === 1 ? "" : "s"} will be revoked. The user can sign in again with their password and two-factor code.`
        }
        confirmLabel={confirmation === "suspend" ? "Suspend account" : "Revoke sessions"}
        danger={confirmation === "suspend"}
        loading={confirmation === "suspend" ? update.isPending : revoke.isPending}
        error={confirmation === "suspend" ? update.error : revoke.error}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

function StaffCard({
  userId,
  current,
  isSelf,
}: {
  userId: string;
  current: PlatformRole | null;
  isSelf: boolean;
}) {
  const update = useUpdateUser(userId);
  const [role, setRole] = useState<string>(current ?? "");

  return (
    <Card>
      <CardHeader title="Platform access" description="Staff roles are separate from tenant roles." />
      <div className="space-y-4 px-5 py-4">
        <Field
          label="Platform role"
          htmlFor="platform-role"
          hint={
            isSelf
              ? "You cannot remove your own superadmin role."
              : "Support can read everything; superadmin can change it."
          }
        >
          <Select
            id="platform-role"
            value={role}
            disabled={isSelf}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="">No platform access</option>
            <option value="support">Support (read-only)</option>
            <option value="superadmin">Superadmin</option>
          </Select>
        </Field>

        <Button
          className="w-full"
          disabled={isSelf || role === (current ?? "")}
          loading={update.isPending}
          onClick={() =>
            update.mutate({ platformRole: role === "" ? null : (role as PlatformRole) })
          }
        >
          Update access
        </Button>
      </div>
    </Card>
  );
}

function DangerZone({ userId, email }: { userId: string; email: string }) {
  const remove = useDeleteUser();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");

  const orphaned =
    remove.error instanceof ApiError &&
    remove.error.details &&
    typeof remove.error.details === "object" &&
    "organizations" in remove.error.details
      ? (remove.error.details.organizations as { id: string; name: string }[])
      : null;

  return (
    <Card className="border-danger/30">
      <CardHeader
        title="Delete account"
        description="Removes the user, their memberships and sessions. Refused if sole owner of an org."
      />
      <div className="space-y-3 px-5 py-4">
        {remove.error && (
          <Alert>
            {remove.error.message}
            {orphaned && (
              <ul className="mt-2 list-inside list-disc">
                {orphaned.map((org) => (
                  <li key={org.id}>
                    <Link to={`/organizations/${org.id}`} className="underline underline-offset-2">
                      {org.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <Field
          label={
            <>
              Type <TypedPhrase>{email}</TypedPhrase> to confirm
            </>
          }
          htmlFor="confirm-user"
        >
          <Input id="confirm-user" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>

        <Button
          variant="danger"
          disabled={confirm !== email}
          loading={remove.isPending}
          onClick={() => remove.mutate(userId, { onSuccess: () => void navigate("/users") })}
        >
          Delete account
        </Button>
      </div>
    </Card>
  );
}

/**
 * The community role behind this account.
 *
 * A community moderator is not a platform role and this panel does not make it
 * one: a Maxlayer superadmin is still not a moderator, cannot see the report
 * queue, and cannot remove a post. What they can do is say who may — deciding
 * who holds the job is the platform owner's, even though the job itself is not
 * part of anybody's employment contract.
 *
 * Absent where no community is configured, and honest where one is configured
 * and unreachable. This is the only thing on this console that depends on a
 * service outside the control plane, so it degrades to a sentence rather than
 * taking the page with it.
 */
export function CommunityCard({ userId, canWrite }: { userId: string; canWrite: boolean }) {
  const { data, isLoading } = useCommunityMembership(userId);
  const setRole = useSetCommunityRole(userId);
  const [role, setRole_] = useState<string>("");

  useEffect(() => {
    if (data?.profile) setRole_(data.profile.role);
  }, [data?.profile]);

  // Nothing to say on a deployment with no community attached, which is most of
  // them: an empty panel is worse than no panel.
  if (isLoading || !data?.configured) return null;

  return (
    <Card>
      <CardHeader
        title="Community"
        /* The second clause is not decoration. A change made here is written to
           the community's own moderation log and read by its admins, which is
           worth knowing before pressing the button — it is attributed to the
           operator console rather than to a name, and this console's audit log
           holds the other half. */
        description="Moderation roles are the community's, not the platform's. Changes show in its moderation log."
      />
      <div className="space-y-4 px-5 py-4">
        {data.unreachable ? (
          <Alert tone="warning">
            The community could not be reached, so its role is not shown. Nothing here has changed.
          </Alert>
        ) : !data.profile ? (
          <p className="text-sm text-fg-muted">
            No community profile. They have to sign in to the community once before there is a role
            to give.
          </p>
        ) : (
          <>
            <Detail label="Handle" value={`@${data.profile.username}`} />
            {data.profile.suspended && (
              <Alert tone="warning">Suspended in the community.</Alert>
            )}

            <Field
              label="Community role"
              htmlFor="community-role"
              hint={
                canWrite
                  ? "A moderator sees the report queue. An admin also appoints moderators."
                  : "Only a superadmin can change this."
              }
            >
              <Select
                id="community-role"
                value={role}
                disabled={!canWrite}
                onChange={(e) => setRole_(e.target.value)}
              >
                <option value="member">Member</option>
                <option value="moderator">Moderator</option>
                <option value="admin">Admin</option>
              </Select>
            </Field>

            {canWrite && (
              <Button
                className="w-full"
                variant="secondary"
                loading={setRole.isPending}
                disabled={role === data.profile.role}
                onClick={() => setRole.mutate(role)}
              >
                Set community role
              </Button>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
